import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';

import type { ClientApi } from '@yandex-int/messenger.sdk';
import type { WidgetEvent } from '@yandex-int/messenger.widget/lib/libs/WidgetEvent/WidgetEvent';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import Button from '../../../../shared/components/button';
import Spin from '../../../../shared/components/spin';
import SvgImage from '../../../../shared/components/svg-image';
import { isMobileUa } from '../../../../shared/features/uatraits/selectors';
import { showChat } from '../../../../shared/helpers';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { useChatWidget } from '../../../../shared/hooks/messenger';
import { getBroadcastLiveData } from '../../features/broadcast/selectors';
import classes from './index.css';

const closeIcon = <SvgImage imageName="closeChatCross" />;

interface BroadcastViewerChatProps {
    visible: boolean;
    onClose(): void;
}

const BroadcastViewerChat: FunctionComponent<BroadcastViewerChatProps> = ({ onClose, visible }) => {
    const { chatId } = useSelector(getBroadcastLiveData);
    const isMobile = useSelector(isMobileUa);

    const container = useRef<HTMLDivElement>(null);

    const [chatUserGuid, setChatUserGuid] = useState<string>();
    const [authUserChanged, setChatAuthUserChanged] = useState<boolean>();
    const [isChatWidgetReady, setIsChatWidgetReady] = useState<boolean>();

    const onChatWidgetError = useCallback(() => {
        countMetrika('stream_viewers_screen', 'chat_error');
    }, []);

    const onCloseClick = useCallback(() => {
        onClose();
    }, [onClose]);

    const onChatUnload = useCallback(() => {
        setChatAuthUserChanged(true);
        setIsChatWidgetReady(false);
    }, [setChatAuthUserChanged, setIsChatWidgetReady]);

    const onChatWidgetReady = useCallback(() => {
        setIsChatWidgetReady(true);
        countMetrika('stream_viewers_screen', 'chat_shown');
    }, [setIsChatWidgetReady]);

    const onChatUserLoaded = useCallback(
        (event: WidgetEvent<ClientApi.UserLoaded>) => {
            const { guid } = event.data ?? {};
            if (guid && chatUserGuid !== guid) {
                setChatUserGuid(guid);
            }
        },
        [chatUserGuid, setChatUserGuid]
    );

    useEffect(() => {
        if (!chatId) {
            return;
        }

        if (isChatWidgetReady) {
            showChat({ chatId });
        }
    }, [isChatWidgetReady, chatId, authUserChanged]);

    const { isWidgetLoading } = useChatWidget({
        chatId: chatId || null,
        mountNode: container.current,
        userLoaded: onChatUserLoaded,
        ready: onChatWidgetReady,
        unload: onChatUnload,
        error: onChatWidgetError,
    });

    return (
        <div
            className={cn(classes.BroadcastViewerChat, {
                [classes.BroadcastViewerChat_visible]: visible,
            })}
        >
            {isMobile ? (
                <Button
                    className={classes.BroadcastViewerChat__Close}
                    view="neutral-transparent"
                    size="sm"
                    icon={closeIcon}
                    onClick={onCloseClick}
                />
            ) : null}
            <div className={classes.BroadcastViewerChat__Wrapper} ref={container} />
            {isWidgetLoading ? (
                <div className={classes.BroadcastViewerChat__SpinWrapper}>
                    <Spin className={classes.BroadcastViewerChat__Spin} progress size="lg" color="cyan" />
                </div>
            ) : null}
        </div>
    );
};

export default BroadcastViewerChat;
